import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import type { ColumnDef, SortingState } from '@tanstack/react-table';
import type { AuthMeResponse } from 'api/auth/Auth.schemas.ts';
import cx from 'clsx';
import dayjs from 'common/dayjs.ts';
import { getLegStatusColor, getRuntimeTimezone, MPOST, POST, parseFormValues } from 'common/helpers.ts';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import useSWR from 'swr';
import useSWRInfinite from 'swr/infinite';
import useSWRMutation from 'swr/mutation';
import type { LEG_STATUS, Leg } from 'types/Leg.ts';
import { Button } from 'ui/component/Button.tsx';
import { IClock, IDots, IHandOff, IPrint } from 'ui/component/Icons.tsx';
import { Modal } from 'ui/component/Modal.tsx';
import { Stepper } from 'ui/component/Stepper.tsx';
import { Tag } from 'ui/component/Tag.tsx';
import { Tooltip } from 'ui/component/Tooltip.tsx';
import { Datetime } from '#admin/component/Datetime.tsx';
import { useConfirm } from '#admin/component/DialogProvider.tsx';
import { getSWRKeyForPage, Table } from '#admin/component/table/index.ts';
import { useMe } from '#admin/hook/useMe.tsx';

type Props = {
  driver_id: string | null;
  driver_name: string | null;
  onClose?: () => void;
  isOpen: boolean;
};

const getColumns = (
  me: AuthMeResponse | undefined,
  t: any,
  legs: Leg[] | undefined,
  hasManualReordering: boolean,
  onUnassign: (legId: string) => void,
  unassigningId: string | null,
): ColumnDef<Leg>[] => {
  if (!me || !legs) return [];

  return [
    {
      accessorKey: 'shipment_logistics_ex_id',
      header: t('labels.orderNumber'),
      size: 150,
      cell: (info) => {
        const value = info.getValue<string>();
        const leg = info.row.original;
        const now = dayjs();
        const nextETATime = !leg.origin_actual_datetime ? dayjs(leg.origin_expected_datetime) : dayjs(leg.destination_expected_datetime);
        const nextETA = nextETATime.diff(now, 'minutes');

        let is_delayed_tooltip = '';
        let is_delayed_color = '';
        if (nextETA < 0) {
          is_delayed_tooltip = t('tooltips.delayedShipment');
          is_delayed_color = 'text-red-dark';
        } else if (nextETA < 30) {
          is_delayed_tooltip = t('tooltips.nextETA', { minutes: nextETA });
          is_delayed_color = 'text-yellow';
        }

        return (
          <Link className="anchor anchor-novisit flex flex-row items-center gap-1" to={`/shipment/${info.row.original.shipment_id}`}>
            {value}
            <div className="flex flex-row gap-0">{is_delayed_tooltip && <IClock className={is_delayed_color} title={is_delayed_tooltip} />}</div>
          </Link>
        );
      },
    },
    {
      id: 'leg_number',
      header: t('labels.legNumber'),
      // as LegList, plus room for the manual-reorder icon that can sit beside the stepper
      size: 160,
      enableSorting: false,
      meta: { hideFallback: true },
      cell: (info) => {
        const leg = info.row.original;
        if (!leg.ordinal) return null; // Let fallback handle it

        return (
          <div className="flex flex-row items-center gap-2">
            <Stepper
              className="w-[100px]"
              steps={[String(leg.ordinal), String(leg.ordinal + 1)]}
              currentStep={leg.status === 'delivered' ? String(leg.ordinal + 1) : leg.status === 'transiting' ? String(leg.ordinal) : 'xyz'}
              currentLegStatus={leg.status}
              disableRing={true}
            />
            {hasManualReordering && leg.run_order === null && (
              <Tooltip text={t('labels.noManualReorder')}>
                <IHandOff className="text-gray-dark" size={20} />
              </Tooltip>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: 'origin_address_name',
      header: t('labels.from'),
      size: 170,
      meta: { truncate: true },
    },
    {
      accessorKey: 'origin_expected_datetime',
      header: t('labels.collectionAt'),
      size: 180,
      cell: (info) => {
        const leg = info.row.original;
        return (
          <Datetime timezone={leg.origin_tz || getRuntimeTimezone()} truncateRegion={true} layout={'y'}>
            {leg.origin_expected_datetime}
          </Datetime>
        );
      },
    },
    {
      accessorKey: 'destination_address_name',
      header: t('labels.to'),
      size: 170,
      meta: { truncate: true },
    },
    {
      accessorKey: 'destination_expected_datetime',
      header: t('labels.deliveryAt'),
      size: 180,
      cell: (info) => {
        const leg = info.row.original;
        return (
          <Datetime timezone={leg.destination_tz || getRuntimeTimezone()} truncateRegion={true} layout={'y'}>
            {leg.destination_expected_datetime}
          </Datetime>
        );
      },
    },
    {
      accessorKey: 'status',
      header: t('labels.status'),
      size: 140,
      cell: (info) => {
        const value = info.getValue<LEG_STATUS>();
        const renderTag = (status: LEG_STATUS) => <Tag className={cx('!p-2', getLegStatusColor(status))}>{t(`status.${status}`)}</Tag>;
        return renderTag(value);
      },
    },
    {
      id: 'actions',
      size: 40,
      meta: { className: '!p-0', hideFallback: true },
      cell: (info) => {
        const leg = info.row.original;
        return (
          <Menu>
            <MenuButton className="flex size-full place-content-center place-items-center" data-table-no-row-events>
              <IDots className="text-gray-400" size={28} data-table-no-row-events />
            </MenuButton>
            <MenuItems className="row-menu" anchor="bottom end">
              <MenuItem>
                <button className="!border-0 row-menu-item" disabled={unassigningId === leg.id} onClick={() => onUnassign(leg.id)}>
                  {t('labels.unassign')}
                </button>
              </MenuItem>
            </MenuItems>
          </Menu>
        );
      },
    },
  ];
};

const NEXT_HOURS_OPTIONS = [24, 48, 72, 96] as const;
type NextHoursOption = (typeof NEXT_HOURS_OPTIONS)[number];

export const DriverLegsDialog = ({ driver_id, driver_name, onClose, isOpen }: Props) => {
  const { me } = useMe();
  const { t } = useTranslation();
  const confirm = useConfirm();
  const [sorters, setSorters] = React.useState<SortingState>([{ id: 'origin_expected_datetime', desc: false }]);
  const [nextHours, setNextHours] = React.useState<NextHoursOption | null>(null);
  const filters = React.useMemo(() => {
    if (!nextHours) return { driver_id, live: true };
    return { driver_id, live: true, next_eta$to: dayjs().add(nextHours, 'hour').toISOString() };
  }, [driver_id, nextHours]);
  const swr = useSWRInfinite(getSWRKeyForPage('/leg/list', 50, filters, sorters, parseFormValues), POST);
  const { data: rowsCount, mutate: mutateRowsCount } = useSWR(driver_id ? ['/leg/count', filters, parseFormValues] : null, POST);
  const [unassigningId, setUnassigningId] = React.useState<string | null>(null);
  const reorderSWR = useSWRMutation('/leg/reorder', MPOST);
  const unassignSWR = useSWRMutation('/leg/unassign-driver', (_key: string, { arg }: { arg: { legId: string } }) => MPOST(`/leg/${arg.legId}/unassign-driver`, { arg: {} }));
  const legs: Leg[] = React.useMemo(() => swr.data?.flat() || [], [swr]);
  const hasManualReordering = legs.some((leg) => leg.run_order !== null);

  const handleUnassign = React.useCallback(
    async (legId: string) => {
      if (!(await confirm(t('dialogs.driver.unassignConfirm')))) return;
      setUnassigningId(legId);
      try {
        await unassignSWR.trigger({ legId });
        await Promise.all([swr.mutate(swr.data?.map((page: Leg[]) => page.filter((leg: Leg) => leg.id !== legId)) ?? [], { revalidate: true }), mutateRowsCount()]);
      } finally {
        setUnassigningId(null);
      }
    },
    [confirm, t, unassignSWR, swr, mutateRowsCount],
  );

  const columns = React.useMemo(
    () => getColumns(me, t, legs, hasManualReordering, handleUnassign, unassigningId),
    [me, t, legs, hasManualReordering, handleUnassign, unassigningId],
  );

  const onReorder = (newOrder: any[]) => {
    reorderSWR.trigger({
      driver_id,
      order: newOrder.map((leg, index) => ({ id: leg.id, run_order: index })),
    });
  };

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const escape = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

    const formatDate = (datetime: string | null, tz: string) => {
      if (!datetime) return '-';
      const formatted = dayjs.tz(new Date(datetime), tz).format('YYYY-MM-DD HH:mm');
      const tzLabel = tz.includes('/') ? tz.split('/')[1] : tz;
      return `${formatted} (${t('labels.timezoneTime', { timezone: tzLabel })})`;
    };

    const tableRows = legs
      .map((leg) => {
        const originTz = leg.origin_tz || getRuntimeTimezone();
        const destTz = leg.destination_tz || getRuntimeTimezone();
        return `<tr>
          <td>${escape(leg.shipment_logistics_ex_id || '-')}</td>
          <td>${escape(leg.origin_address_name || '-')}</td>
          <td>${escape(formatDate(leg.origin_expected_datetime, originTz))}</td>
          <td>${escape(leg.destination_address_name || '-')}</td>
          <td>${escape(formatDate(leg.destination_expected_datetime, destTz))}</td>
          <td>${escape(t(`status.${leg.status}`))}</td>
        </tr>`;
      })
      .join('');

    const escapedName = escape(driver_name || '');
    printWindow.document.write(`<!DOCTYPE html>
<html>
  <head>
    <title>Driver Legs: ${escapedName}</title>
    <style>
      body { font-family: Arial, sans-serif; padding: 20px; font-size: 12px; }
      h2 { margin-bottom: 4px; font-size: 16px; }
      p { margin: 0 0 16px; font-size: 11px; color: #666; }
      table { border-collapse: collapse; width: 100%; }
      th, td { border: 1px solid #ccc; padding: 6px 8px; text-align: left; }
      th { background: #f5f5f5; font-weight: bold; }
      tr:nth-child(even) { background: #fafafa; }
    </style>
  </head>
  <body>
    <h2>Driver Legs: ${escapedName}</h2>
    <p>${escape(t('dialogs.driverLegs.description'))}</p>
    <table>
      <thead>
        <tr>
          <th>${escape(t('labels.orderNumber'))}</th>
          <th>${escape(t('labels.from'))}</th>
          <th>${escape(t('labels.collectionAt'))}</th>
          <th>${escape(t('labels.to'))}</th>
          <th>${escape(t('labels.deliveryAt'))}</th>
          <th>${escape(t('labels.status'))}</th>
        </tr>
      </thead>
      <tbody>${tableRows}</tbody>
    </table>
    <script>window.onload = function() { window.print(); window.close(); }</script>
  </body>
</html>`);
    printWindow.document.close();
  };

  const printButton = (
    <Button small onClick={handlePrint} LeftIcon={IPrint} className="!w-auto">
      {t('labels.print')}
    </Button>
  );

  return (
    <Modal
      key={driver_id}
      title={<h4>{t('dialogs.driverLegs.title', { driver_name })}</h4>}
      headerActions={printButton}
      description={t('dialogs.driverLegs.description')}
      isOpen={isOpen}
      onClose={onClose}
    >
      <div className="mx-auto w-auto max-w-[calc(100vw-180px)]">
        <div className="mb-3 flex items-center gap-2">
          <label className="font-medium text-sm">{t('dialogs.driverLegs.filterNext')}</label>
          <select
            value={nextHours ?? ''}
            onChange={(e) => setNextHours(e.target.value ? (Number(e.target.value) as NextHoursOption) : null)}
            className="h-9 min-w-[130px] border border-grey-border py-1 pr-8 pl-2 text-sm"
          >
            <option value="">{t('labels.all')}</option>
            {NEXT_HOURS_OPTIONS.map((h) => (
              <option key={h} value={h}>
                {t('dialogs.driverLegs.filterNextHours', { hours: h })}
              </option>
            ))}
          </select>
        </div>
        <Table
          className={cx('max-h-[calc(100vh-350px)]', !legs?.length && 'min-h-[300px]')}
          columns={columns}
          swr={swr}
          pagination={true}
          rowsCount={rowsCount ?? undefined}
          defaultSorting={sorters}
          onColumnSort={setSorters}
          estimateSize={62}
          overscan={50}
          columnVisibility={{ supplier_id: false, driver_id: false }}
          onReorder={onReorder}
        />
      </div>
    </Modal>
  );
};
